<template>
    <div class="modal-card add_company_card">
        <header class="modal-card-head has-background-white">
            <h3 class="modal-card-title has-text-centered">
                Añadir compañía
            </h3>
            <button class="delete" aria-label="Cerrar Modal" @click="$emit('close')"></button>
        </header>
        <section class="section_filter centered_div">
            <table>
                <tr>
                    <td class="filtrar_text" colspan="2">Crear compañia:</td>
                </tr>
                <tr v-if="!is_consolidated">
                    <td colspan="2">
                        <label class="foreign_toggle">
                            <input v-model="is_foreign" type="checkbox" name="add_company_is_foreign" />
                            Empresa extranjera (sin RUT chileno)
                        </label>
                    </td>
                </tr>
                <tr>
                    <td colspan="2">
                        <label class="foreign_toggle">
                            <input v-model="is_consolidated" type="checkbox" name="add_company_is_consolidated" />
                            Empresa consolidada (solo lectura: agrega los datos de otras empresas)
                        </label>
                    </td>
                </tr>
                <tr v-if="!is_consolidated">
                    <td class="input_title">{{ identifierLabel }}</td>
                    <td>
                        <div class="input_surround">
                            <!-- key fuerza remontar el Input al cambiar de universo:
                                 las rules de vee-validate no son reactivas ahí adentro. -->
                            <Input :key="is_foreign ? 'tax_id' : 'rut'" :rules="identifierRules" type="text"
                                :placeholder="identifierPlaceholder" input-id="rut" name="rut" ref="rut"
                                v-model="company_rut" :specificClass="true" inputClasses="input_company" />
                        </div>
                    </td>
                </tr>
                <tr v-else>
                    <td colspan="2">
                        <span class="consolidated_hint">
                            El identificador se genera automáticamente. Después de crearla,
                            elige sus empresas miembro desde el panel (con verificación).
                        </span>
                    </td>
                </tr>
                <tr>
                    <td class="input_title">Nombre Compañia:</td>
                    <td>
                        <div class="input_surround">
                            <Input rules="required" type="text" placeholder="(Razón Social)" input-id="company_name"
                                name="company_name" ref="company_name" v-model="company_name" :specificClass="true"
                                :rutMask="rutMask" inputClasses="input_company" />
                        </div>
                    </td>
                </tr>
                <tr>
                    <td colspan="2">
                        <div>
                            <span class="message">
                                {{ message }}
                            </span>
                        </div>
                    </td>
                </tr>
                <tr>
                    <td colspan="2">
                        <button :disabled="createDisabled" @click="crearCompania()"
                            :class="['grabar_compania', { 'disabled_button': createDisabled }]">
                            Crear Compañia
                        </button>
                    </td>
                </tr>
            </table>
        </section>
        <section class="section-companies">
            <div role="navigation" aria-label="main navigation" class="analyst_users">
                <table>
                    <tr v-for="companies_sub_list in companies_list" v-bind:key="companies_sub_list[0].id">
                        <td v-for="company in companies_sub_list" v-bind:key="company.id" class="company_td">
                            <div class="long_company_name">
                                {{ company.rut + ' ' + company.name }}
                            </div>
                            <div class="nombre_email nobreak">
                                {{ company.email }}
                            </div>
                            <div class="nombre_email nobreak">
                                {{ company.nombre + ' ' + company.apellido }}
                            </div>
                        </td>
                    </tr>
                </table>
            </div>
        </section>
    </div>
</template>

<script>
import {
    allCompanies,
    saveCompany,
    saveConsolidatedCompany,
} from "@/api/carteraAnalista";
import { consoleHandlerError } from "@/utils/message";
import Input from "@/components/inputs/Input";

export default {
    name: "AddCompany",
    components: {
        Input,
    },
    data() {
        return {
            raw_companies_list: [],
            company_rut: "",
            company_name: "",
            is_foreign: false,
            is_consolidated: false,
            searchTimer: null,
            message: "",
            loading: false,
            rutMask: ["#######-B", "########-B", "#########-B"],
        };
    },
    computed: {
        // El backend ya filtró y acotó los resultados (búsqueda server-side); acá
        // sólo particionamos. El botón "Crear" se habilita cuando NO hay matches
        // (guard de dedup: si el RUT/nombre ya existe, no dejamos crear).
        companies_list() {
            return this.partition(this.raw_companies_list);
        },
        identifierLabel() {
            return this.is_foreign ? "Identificador:" : "Rut:";
        },
        identifierRules() {
            // Chilena → regla rut intacta; extranjera → genérica (el backend
            // re-valida con TaxIdValidator).
            return this.is_foreign
                ? "required|min:4|max:24"
                : "required|min:10|max:12|rut";
        },
        identifierPlaceholder() {
            return this.is_foreign ? "Ej. 30712345678" : "12345678-9";
        },
        createDisabled() {
            // Acumulada: solo exige nombre (el identificador lo acuña el
            // backend; el dedup por RUT no aplica).
            if (this.is_consolidated) {
                return !this.company_name.trim();
            }
            // El guard de dedup por matches aplica solo al universo chileno: un
            // homónimo chileno NO debe bloquear el alta de una extranjera (el
            // backend dedup-ea dentro de su universo de todos modos).
            return !this.is_foreign && this.companies_list.length > 0;
        },
    },
    methods: {
        calcSize(largo) {
            return Math.ceil(Math.log(largo + 1));
        },
        partition(list) {
            const result = [];
            const largo = list.length;
            const size = this.calcSize(largo);
            for (let i = 0; i < largo; i = i + size) {
                result.push(list.slice(i, i + size));
            }
            return result;
        },
        // Debounce: busca en el server por RUT o nombre (antes bajaba ~100k
        // compañías y filtraba en el cliente).
        scheduleSearch() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => this.searchCompanies(), 250);
        },
        async searchCompanies() {
            const term = this.company_rut.trim() || this.company_name.trim();
            if (!term) {
                this.raw_companies_list = [];
                return;
            }
            try {
                const { data } = await allCompanies(true, term);
                this.raw_companies_list = data;
            } catch (error) {
                consoleHandlerError({ error, silent: true });
            }
        },
        async crearCompania() {
            this.loading = true;
            try {
                const { data } = this.is_consolidated
                    ? await saveConsolidatedCompany(this.company_name)
                    : await saveCompany(
                        this.company_rut,
                        this.company_name,
                        this.is_foreign
                    );
                this.message = data.msg;
            } catch (error) {
                this.message = "No se pudo crear la compañía.";
                consoleHandlerError({ error, silent: true });
            } finally {
                this.loading = false;
            }
        },
    },
    beforeDestroy() {
        clearTimeout(this.searchTimer);
    },
    watch: {
        company_rut() {
            this.scheduleSearch();
        },
        company_name() {
            this.scheduleSearch();
        },
        // Al cambiar de universo el identificador tipeado no aplica (formato y
        // validación distintos): se limpia para no arrastrar un valor a medias.
        is_foreign() {
            this.company_rut = "";
        },
        // Acumulada: sin identificador manual ni universo extranjero.
        is_consolidated(value) {
            this.company_rut = "";
            if (value) {
                this.is_foreign = false;
            }
        },
    },
};
</script>
<style scoped lang="scss">
// Tema del analista (var(--ap-*), elements/_analyst_theme.scss).
.add_company_card {
    font-family: var(--ap-font);
    color: var(--ap-ink-2);
    width: min(560px, 94vw);
    display: flex;
    flex-direction: column;
    max-height: 86vh;
    background-color: var(--ap-paper);
    border-radius: var(--ap-radius);
    overflow: hidden;
}

:deep(.modal-card-title){
    font-family: var(--ap-font);
    font-weight: 700;
    font-size: 20px;
    letter-spacing: -0.01em;
    color: var(--ap-ink);
}

.section_filter {
    padding: 22px;
    border-bottom: 1px solid var(--ap-line);
    background-color: var(--ap-paper);

    table {
        width: 100%;

        td {
            border: none;
        }
    }
}

.filtrar_text {
    padding-bottom: 6px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ap-muted);
}

.foreign_toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0 2px;
    font-size: 13px;
    color: var(--ap-slate);
    cursor: pointer;
    user-select: none;

    input {
        accent-color: var(--ap-teal);
        cursor: pointer;
    }
}

.input_title {
    padding: 12px 12px 0 0;
    text-align: right;
    font-size: 13px;
    font-weight: 500;
    color: var(--ap-slate);
    white-space: nowrap;
}

.input_surround {
    margin: 6px 0 8px;
}

// Input custom (RUT / nombre): lo alineamos al tema dentro del scope.
:deep(.input_company){
    width: 100%;
    padding: 9px 12px !important;
    line-height: normal !important;
    margin: 0 !important;
    border: 1px solid var(--ap-line-strong) !important;
    border-radius: var(--ap-radius-sm) !important;
    font-family: var(--ap-font) !important;
    font-size: 13.5px !important;
    color: var(--ap-ink) !important;
    box-shadow: none !important;
    transition: border-color 0.14s var(--ap-ease), box-shadow 0.14s var(--ap-ease);

    &:focus {
        border-color: var(--ap-teal) !important;
        box-shadow: 0 0 0 3px var(--ap-teal-wash) !important;
    }
}

.grabar_compania {
    margin-top: 6px;
    width: 100%;
    height: 42px;
    border: none;
    border-radius: var(--ap-radius-pill);
    background-color: var(--ap-ink);
    color: var(--ap-paper);
    font-family: var(--ap-font);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.14s var(--ap-ease), background-color 0.14s var(--ap-ease);

    &:hover {
        transform: translateY(-1px);
        background-color: var(--ap-teal-deep);
    }

    &:active {
        transform: translateY(0);
    }
}

.disabled_button {
    background-color: var(--ap-mist);
    color: var(--ap-muted);
    cursor: not-allowed;

    &:hover {
        transform: none;
        background-color: var(--ap-mist);
    }
}

.message {
    display: block;
    padding-top: 12px;
    font-size: 13px;
    color: var(--ap-teal-deep);
}

.consolidated_hint {
    display: block;
    padding: 6px 0 2px;
    font-size: 12.5px;
    color: var(--ap-slate);
}

// Listado de compañías del sistema — grid responsivo (la <table> particionada
// desbordaba a lo ancho; display:contents disuelve tr/tbody en un grid).
.section-companies {
    flex: 1;
    overflow: hidden auto; // X oculto: sin scrollbar horizontal espurio
    padding: 16px 22px 22px;
    background-color: var(--ap-paper);

    :deep(table){
        display: block;
        width: 100%;
    }

    :deep(tbody){
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
        gap: 8px;
    }

    :deep(tr){
        display: contents;
    }
}

.company_td {
    min-width: 0;
    padding: 11px 13px;
    border: 1px solid var(--ap-line);
    border-radius: var(--ap-radius-sm);
    background-color: var(--ap-paper);
}

.long_company_name {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13.5px;
    font-weight: 600;
    color: var(--ap-ink);
}

.nombre_email {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    color: var(--ap-slate);
}

.nobreak {
    white-space: nowrap;
}
</style>
